import { useEffect, useRef, useState } from 'react';
import { OFFLINE_VIDEO_ADVICE, OFFLINE_VIDEO_MAX_BYTES, storageSize } from '@wr/shared';
import { ApiError, del, errorMessage } from '../../api.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, Notice } from '../../components/ui.js';
import { shortDate } from '../leads/LeadSheet.js';
import type { Asset } from './ContentScreen.js';
import {
  UploadStopped,
  cancelUpload,
  sendOfflineCopy,
  unfinishedUpload,
  type OfflineUpload,
} from './offlineUpload.js';

type Phase =
  | { kind: 'idle' }
  | { kind: 'sending'; file: File; sent: number; total: number }
  | { kind: 'paused'; file: File; sent: number; total: number; why: 'offline' | 'stopped' };

/**
 * A video's offline copy (round 2; content admins): an MP4 the admin
 * downloads from our own YouTube channel in YouTube Studio and adds here,
 * played on the devices at shows without network. Sent straight to Spaces
 * with its progress; a cut connection goes on by itself when the network
 * is back, and a closed page resumes with the same file.
 */
export function OfflineCopy({ asset: a, onChanged }: { asset: Asset; onChanged(): void }) {
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [left, setLeft] = useState<OfflineUpload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const stop = useRef<AbortController | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let current = true;
    unfinishedUpload(a.id).then(
      (u) => current && setLeft(u),
      () => undefined,
    );
    return () => {
      current = false;
      stop.current?.abort();
    };
  }, [a.id]);

  async function send(file: File) {
    setError(null);
    if (!/\.mp4$/i.test(file.name) && file.type !== 'video/mp4') {
      setError('An offline copy is an MP4 video.');
      return;
    }
    if (file.size > OFFLINE_VIDEO_MAX_BYTES) {
      setError(
        `This file is ${storageSize(file.size)}: an offline copy is 1 GB at most. Export it in 720p.`,
      );
      return;
    }
    const controller = new AbortController();
    stop.current = controller;
    setPhase({ kind: 'sending', file, sent: 0, total: file.size });
    try {
      await sendOfflineCopy(a.id, file, {
        signal: controller.signal,
        onProgress: (sent, total) => setPhase({ kind: 'sending', file, sent, total }),
      });
      setPhase({ kind: 'idle' });
      setLeft(null);
      toast('Offline copy added. Devices can now keep this video for shows without network.');
      onChanged();
    } catch (err) {
      if (err instanceof UploadStopped) {
        setPhase((p) => ({
          kind: 'paused',
          file,
          sent: p.kind === 'idle' ? 0 : p.sent,
          total: file.size,
          why: err.reason,
        }));
      } else {
        setPhase({ kind: 'idle' });
        const message = err instanceof ApiError ? err.body.message : undefined;
        setError(typeof message === 'string' ? message : errorMessage(err));
      }
    }
  }

  // Back online: a sending stopped by the network goes on by itself.
  useEffect(() => {
    if (phase.kind !== 'paused' || phase.why !== 'offline') return;
    const resume = () => void send(phase.file);
    window.addEventListener('online', resume, { once: true });
    return () => window.removeEventListener('online', resume);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  async function remove() {
    setConfirm(false);
    try {
      await del(`/content/${a.id}/offline`);
      toast('Offline copy removed. Devices drop theirs at their next sync.');
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function dropLeft() {
    if (!left) return;
    await cancelUpload(a.id, left.id).catch(() => undefined);
    setLeft(null);
  }

  const busy = phase.kind === 'sending';
  const pct = phase.kind === 'idle' ? 0 : Math.round((phase.sent / Math.max(1, phase.total)) * 100);
  return (
    <section className="offline-copy" aria-label="Offline copy">
      <h3 className="sec">Offline copy</h3>
      {a.has_offline ? (
        <p className="note">
          {storageSize(a.offline_size_bytes ?? 0)} MP4, played on devices without network
          {a.offline_added_at ? ` (added ${shortDate(a.offline_added_at)})` : ''}.
        </p>
      ) : (
        <p className="note">
          None yet: without network, this video does not play. The portal never downloads anything
          from YouTube or Vimeo: add the MP4 of our own channel.
        </p>
      )}
      <p className="note offline-advice">{OFFLINE_VIDEO_ADVICE}</p>
      {left && phase.kind === 'idle' && (
        <Notice kind="warn">
          An upload was interrupted ({left.file_name}, {storageSize(left.size_bytes)},{' '}
          {Math.round((left.done.reduce((n, p) => n + p.size, 0) / left.size_bytes) * 100)}%
          received). Choose the same file to resume it, or{' '}
          <button type="button" className="linkbtn" onClick={() => void dropLeft()}>
            cancel it
          </button>
          .
        </Notice>
      )}
      {phase.kind !== 'idle' && (
        <div className={`uprow ${phase.kind === 'paused' ? 'err' : 'up'}`} aria-live="polite">
          <div className="nm">
            <span>{phase.file.name}</span>
            <span>
              {storageSize(phase.sent)} of {storageSize(phase.total)} ({pct}%)
            </span>
          </div>
          <div
            className="bar"
            role="progressbar"
            aria-label="Offline copy sent"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <i style={{ width: `${pct}%` }} />
          </div>
          {phase.kind === 'paused' && (
            <div className="small">
              {phase.why === 'offline'
                ? 'The connection dropped: it goes on by itself when the network is back.'
                : 'Paused.'}
            </div>
          )}
        </div>
      )}
      {error && <Notice kind="error">{error}</Notice>}
      <div className="actions">
        <input
          ref={input}
          type="file"
          hidden
          accept="video/mp4,.mp4"
          aria-label="Offline copy file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void send(file);
          }}
        />
        {busy && (
          <button type="button" className="btn sm" onClick={() => stop.current?.abort()}>
            Pause
          </button>
        )}
        {phase.kind === 'paused' && (
          <button type="button" className="btn sm primary" onClick={() => void send(phase.file)}>
            Resume
          </button>
        )}
        {phase.kind === 'idle' && (
          <button type="button" className="btn sm" onClick={() => input.current?.click()}>
            {a.has_offline ? 'Replace the MP4' : 'Add MP4'}
          </button>
        )}
        {phase.kind === 'idle' && a.has_offline && (
          <button type="button" className="btn sm danger" onClick={() => setConfirm(true)}>
            Remove
          </button>
        )}
      </div>
      {confirm && (
        <Dialog
          small
          title="Remove the offline copy?"
          onClose={() => setConfirm(false)}
          footer={
            <>
              <button type="button" className="btn" autoFocus onClick={() => setConfirm(false)}>
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={() => void remove()}>
                Remove
              </button>
            </>
          }
        >
          <p className="dlg-text">
            Devices drop their copy at their next sync: without network, this video will no longer
            play.
          </p>
        </Dialog>
      )}
    </section>
  );
}
